import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';

export const CommissionConfig = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [catalogs, setCatalogs] = useState({ policy_types: [], plans: [] });
  
  const [formData, setFormData] = useState({
    policy_type: '',
    plan: '',
    year_1: 0,
    year_2: 0,
    year_3: 0,
    year_4: 0,
    year_5: 0,
    year_6: 0,
    year_7: 0,
    year_8: 0,
    year_9: 0,
    year_10: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [configsRes, typesRes, plansRes] = await Promise.all([
        api.get('/commission-config'),
        api.get('/catalogs/policy_types'),
        api.get('/plans')
      ]);
      
      setConfigs(configsRes.data);
      setCatalogs({
        policy_types: typesRes.data,
        plans: plansRes.data
      });
    } catch (error) {
      toast.error('Error al cargar configuraciones');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.policy_type || !formData.plan) {
      toast.error('Tipo de póliza y Plan son obligatorios');
      return;
    }

    try {
      if (editingId) {
        await api.put(`/commission-config/${editingId}`, formData);
        toast.success('Configuración actualizada');
      } else {
        await api.post('/commission-config', formData);
        toast.success('Configuración creada');
      }
      
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleEdit = (config) => {
    setFormData({
      policy_type: config.policy_type,
      plan: config.plan,
      year_1: config.year_1,
      year_2: config.year_2,
      year_3: config.year_3,
      year_4: config.year_4,
      year_5: config.year_5,
      year_6: config.year_6,
      year_7: config.year_7,
      year_8: config.year_8,
      year_9: config.year_9,
      year_10: config.year_10
    });
    setEditingId(config.id);
    setShowNewForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta configuración?')) return;
    
    try {
      await api.delete(`/commission-config/${id}`);
      toast.success('Configuración eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setFormData({
      policy_type: '',
      plan: '',
      year_1: 0,
      year_2: 0,
      year_3: 0,
      year_4: 0,
      year_5: 0,
      year_6: 0,
      year_7: 0,
      year_8: 0,
      year_9: 0,
      year_10: 0
    });
    setEditingId(null);
    setShowNewForm(false);
  };

  const handleYearChange = (year, value) => {
    const numValue = parseFloat(value) || 0;
    setFormData(prev => ({ ...prev, [`year_${year}`]: numValue }));
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Configuración de Comisiones
          </h1>
          <p className="text-sm leading-relaxed text-slate-600 mt-2">
            Define los porcentajes de comisión por tipo de póliza, plan y año de vigencia
          </p>
        </div>
        <Button
          onClick={() => setShowNewForm(!showNewForm)}
          className="bg-[#2537b0] hover:bg-[#1e2b8f] text-white rounded-[10px]"
        >
          <Plus size={20} className="mr-2" />
          Nueva Configuración
        </Button>
      </div>

      {/* Form for new/edit */}
      {showNewForm && (
        <Card className="bg-white border border-[#e0e0e0] rounded-[10px] shadow-sm p-6 mb-6">
          <h2 className="text-xl font-medium text-slate-800 mb-4">
            {editingId ? 'Editar Configuración' : 'Nueva Configuración'}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Tipo de Póliza*</Label>
              <Select value={formData.policy_type} onValueChange={(value) => setFormData(prev => ({ ...prev, policy_type: value }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecciona tipo..." />
                </SelectTrigger>
                <SelectContent>
                  {catalogs.policy_types.map((type) => (
                    <SelectItem key={type.id} value={type.name}>{type.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Plan*</Label>
              <Select value={formData.plan} onValueChange={(value) => setFormData(prev => ({ ...prev, plan: value }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecciona plan..." />
                </SelectTrigger>
                <SelectContent>
                  {catalogs.plans.map((plan) => (
                    <SelectItem key={plan.id} value={plan.name}>{plan.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mb-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Porcentajes de Comisión por Año (%)</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((year) => (
                <div key={year}>
                  <Label className="text-xs font-medium text-slate-600">Año {year}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData[`year_${year}`]}
                    onChange={(e) => handleYearChange(year, e.target.value)}
                    className="mt-1"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={resetForm}>
              <X size={18} className="mr-1" />
              Cancelar
            </Button>
            <Button onClick={handleSave} className="bg-[#2537b0] hover:bg-[#1e2b8f] text-white">
              <Save size={18} className="mr-1" />
              Guardar
            </Button>
          </div>
        </Card>
      )}

      {/* List of configurations */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-500">Cargando...</div>
        </div>
      ) : configs.length === 0 ? (
        <Card className="bg-white border border-[#e0e0e0] rounded-[10px] shadow-sm p-12 text-center">
          <p className="text-slate-500 mb-4">No hay configuraciones de comisión</p>
          <p className="text-sm text-slate-400">Crea una configuración para empezar a calcular comisiones</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {configs.map((config) => (
            <Card key={config.id} className="bg-white border border-[#e0e0e0] rounded-[10px] shadow-sm p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    {config.policy_type} - {config.plan}
                  </h3>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(config)}>
                    <Edit2 size={16} className="mr-1" />
                    Editar
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(config.id)} className="text-red-600 hover:text-red-700">
                    <Trash2 size={16} className="mr-1" />
                    Eliminar
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-5 md:grid-cols-10 gap-3">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((year) => (
                  <div key={year} className="text-center">
                    <div className="text-xs font-medium text-slate-500 mb-1">Año {year}</div>
                    <div className="text-sm font-bold text-[#2537b0]">{config[`year_${year}`]}%</div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
